import { AccountInfo, InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser"
import { useMsal } from "@azure/msal-react"
import { useMediaQuery } from "@mui/material"
import { JSX, useEffect, useRef, useState } from "react"
import { RouterProvider } from "react-router-dom"
import { desktopRouter, mobileRouter } from "store/routes"
import { apiConfig } from "./authConfig"
import { HaufeLoadingProvider } from "./contexts/HaufeLoadingContext"

const setVersionTitle = () => {
  document.title = `LIMS`
}

const FullScreenLoader = ({ message = "Loading..." }: { message?: string }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
    }}
  >
    {message}
  </div>
)

const App = (): JSX.Element => {
  setVersionTitle()

  const { instance, accounts, inProgress } = useMsal()
  const [isSessionVerifiedWithBackend, setIsSessionVerifiedWithBackend] = useState(false)
  const [authCheckInProgress, setAuthCheckInProgress] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const lastCheckedAccountRef = useRef<AccountInfo | null>(null)

  useEffect(() => {
    const currentAccount = accounts.length > 0 ? accounts[0] : null

    if (inProgress === InteractionStatus.None) {
      if (currentAccount) {
        // Account logged in
        if (
          currentAccount.homeAccountId !== lastCheckedAccountRef.current?.homeAccountId ||
          !isSessionVerifiedWithBackend
        ) {
          // New account, or same account but not yet verified with backend, or previously failed.
          setIsSessionVerifiedWithBackend(false) // Mark as not verified by backend yet for this account
          setAuthCheckInProgress(true) // Trigger the verification effect
          lastCheckedAccountRef.current = currentAccount // Record that we are initiating a check for this account
        } else {
          // This block is intentionally empty.
          // It represents the stable state where the current user is the same as the last one
          // we checked, and their session has already been successfully verified with the backend.
          // No action is needed.
        }
      } else {
        // No account is active (initial load without SSO, or after logout)
        if (lastCheckedAccountRef.current !== null || !isSessionVerifiedWithBackend) {
          setIsSessionVerifiedWithBackend(true) // Verified as "no active session to check with backend"
          setAuthCheckInProgress(false) // No check needed
          lastCheckedAccountRef.current = null // Clear last checked account
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, inProgress]) // This effect primarily reacts to account changes and MSAL status

  useEffect(() => {
    // This effect performs the backend check when authCheckInProgress is true
    if (!authCheckInProgress || inProgress !== InteractionStatus.None) {
      // If we intended to check but MSAL became busy, keep authCheckInProgress true so it retries when MSAL is free
      // Or, if authCheckInProgress is false, we are done with checks for now.
      return
    }

    const verifySessionWithBackend = async () => {
      const currentAccount = accounts.length > 0 ? accounts[0] : null

      if (!currentAccount) {
        setIsSessionVerifiedWithBackend(true)
        setAuthCheckInProgress(false)
        return
      }

      try {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: apiConfig.scopes,
          account: currentAccount,
        })

        const response = await fetch("/auth/me", {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
            "Content-Type": "application/json",
          },
        })

        if (response.status === 200 || response.ok) {
          setIsSessionVerifiedWithBackend(true)
          setAuthError(null) // Clear any previous auth error
        } else {
          const errorText = await response.text()
          setAuthError(`Backend Auth Failed (${response.status}): ${errorText || "Unknown error"}.`)
          setIsSessionVerifiedWithBackend(false)
        }
      } catch (error: any) {
        if (error instanceof InteractionRequiredAuthError) {
          // For app loading, we might consider the UI "verified" to load, but backend state is uncertain.
          // Or, set an error that suggests interactive login.
          setAuthError("Session requires interaction. Please try logging in again.")
          setIsSessionVerifiedWithBackend(false) // Or true if you want the UI to load and let Apollo handle it
        } else {
          setAuthError(`Session Verification Error: ${error.message || "Unknown error"}`)
          setIsSessionVerifiedWithBackend(false)
        }
      } finally {
        setAuthCheckInProgress(false)
      }
    }

    verifySessionWithBackend()
  }, [authCheckInProgress, accounts, instance, inProgress]) // Run when authCheckInProgress is true & MSAL is idle.

  const isMobile = useMediaQuery("(max-width:758px)")

  if (authCheckInProgress) {
    return <FullScreenLoader message="Verifying session..." />
  }

  if (authError) {
    // You could render a specific error component here instead of FullScreenLoader
    // For now, just showing the error message.
    // This also prevents rendering the main app if auth failed critically BEFORE redirecting to /access-denied
    return (
      <FullScreenLoader
        message={`Authentication Error: ${authError}. Please check console and try logging in again via the login page.`}
      />
    )
  }

  if (accounts.length > 0 && !isSessionVerifiedWithBackend) {
    return <FullScreenLoader message="Finalizing session..." />
  }

  const renderApp = () =>
    isMobile ? <RouterProvider router={mobileRouter} /> : <RouterProvider router={desktopRouter} />

  return <HaufeLoadingProvider>{renderApp()}</HaufeLoadingProvider>
}

export default App
